<!-- 안전리더십 > 컨텐츠수행 자유형식 > 파일업로드 -->
<template>
  <div class="file-upload">
    <v-file-input
      v-model="model"
      label="File input w/ chips"
      chips
      multiple
      @change="onChangeImg"
      hide-details
      :class="{ 'disabled-block': disabled }"
    ></v-file-input>
    <div class="file-list" v-if="displayFiles.length">
      <div
        class="file-item"
        v-for="(f, idx) in displayFiles"
        :key="fileKey(f, idx)">
        <v-chip
          class="file-chip"
          @click="downloadFile(f)">
          {{ fileLabel(f) }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import utils from "@/common/Utils";

const model = defineModel()
const props = defineProps(['disabled'])
const emit = defineEmits(['confirmComment', 'change', 'delete'])

const previousFiles = ref([])

// model은 다형성: File 객체들, 또는 서버파일을 나타내는 {name, url}, 또는 url 문자열 등이 올 수 있다.
// displayFiles는 항상 일관된 형태로 렌더링하기 위한 계산된 값
const displayFiles = computed(() => {
  const val = model.value
  // model이 falsy면 빈배열
  if (!val || !Array.isArray(val)) return []
  return val
})

function onChangeImg(e: Event){
  emit('change', e)
}

// 이름 표시 함수
function fileLabel(f: any) {
  if (!f) return '';
  if (typeof f === 'string') {
    // url 문자열이면 마지막 경로를 파일명으로 사용
    try {
      const url = new URL(f, window.location.href);
      return decodeURIComponent(url.pathname.split('/').pop() || f);
    } catch {
      return f;
    }
  }
  if (f instanceof File) return f.name;
  if (f.name) return f.name;
  if (f.url) {
    try {
      const url = new URL(f.url, window.location.href);
      return decodeURIComponent(url.pathname.split('/').pop() || f.url);
    } catch {
      return f.url;
    }
  }
  return 'file';
}

// 파일 키(동적 리스트에 안정적 key)
function fileKey(f: any, idx: number) {
  if (f instanceof File) return `${f.name}_${f.size}_${idx}`;
  if (typeof f === 'string') return f + '_' + idx;
  if (f?.url) return f.url + '_' + (f.name ?? idx);
  return idx;
}

// 실제 다운로드 처리
async function downloadFile(f: any) {
  try {
    if (!f) return;

    // 1) File 객체일 경우 -> createObjectURL로 바로 다운로드
    if (f instanceof File) {
      const objectUrl = URL.createObjectURL(f);
      triggerAnchorDownload(objectUrl, f.name);
      // revoke는 약간의 delay 후에 하는 것이 안전
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000 * 10);
      return;
    }

    // 2) 문자열(URL)인 경우 -> 바로 링크 클릭 (크로스 도메인/인증 문제 주의)
    if (typeof f === 'string') {
      triggerAnchorDownload(f, fileLabel(f));
      return;
    }

    // 3) 객체({name, url}) 형태
    if (f.url) {
      triggerAnchorDownload(f.url, f.name ?? fileLabel(f));
      return;
    }

    // 4) 그 외 (예: 서버에서 제공하는 토큰이 필요한 경우) - fetch 후 blob으로 다운로드
    // 시나리오: f has {downloadEndpoint, token} 등. 여기서는 일반 fetch fallback 제공
    if (f.downloadEndpoint) {
      const resp = await fetch(f.downloadEndpoint, {
        // 필요하면 헤더에 인증 토큰 추가
        headers: f.token ? { Authorization: `Bearer ${f.token}` } : {},
      });
      if (!resp.ok) throw new Error('Failed to fetch file');
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerAnchorDownload(objectUrl, f.name ?? 'download');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000 * 10);
      return;
    }

    console.warn('Unsupported file type for download', f);
  } catch (err) {
    console.error('downloadFile error', err);
  }
}

// helper: a 태그로 강제 다운로드 트리거
function triggerAnchorDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  // 크로스 도메인일 때 download 어트리뷰트는 동작 안 할 수 있음(브라우저 정책)
  a.download = filename || '';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/* ---------- 삭제 처리 (리스트에서 제거) ---------- */
function removeAt(idx: number) {
  if (!Array.isArray(model)) return;
  const newArr = [...model];
  newArr.splice(idx, 1);
  // v-model에 반영
  // 만약 defineModel()이 v-model 바인딩을 위해 setter를 필요로 하면 그냥 할당
  // @ts-ignore
  model.value = newArr;
  emit('change', newArr);
}

// 삭제 감지용 watch
watch(model, (newVal:any, oldVal:any) => {
  if (Array.isArray(oldVal) && Array.isArray(newVal)) {
    if (oldVal.length > newVal.length) {
      const removed = oldVal.filter(file => !newVal.includes(file))
      utils.log('Deleted file(s):' + removed)
      emit('delete', removed)
    }

    previousFiles.value = [...newVal]
  }
}, { immediate: true })
</script>


<style lang="scss">
.file-upload{
  .v-input{
    display:flex;
    .v-input__prepend{
      margin:0;
      flex-shrink:0;
      .v-icon{
        display:block;
        width:auto;
        padding:0 2rem;
        height:4rem;
        line-height:5rem;
        text-align:center;
        color:var(--color-white);
        background: var(--color-main);
        border-radius:.5rem;
        opacity:1;
        margin-right:2rem;
        &:before{
          content:t('message.add');
          display:block;
          font-size:1.5rem;
          color:var(--color-white);
          line-height:4rem;
        }
      }
    }
    .v-field{
      .v-field__overlay,
      .v-field__loader,
      .v-label,
      .v-field__outline{
        display:none;
      }
      .v-chip{
        font-size:1.2rem
      }
    }
    .v-input__details{
      display:none;
    }
    .v-field__input{
      padding:.8rem 0;
      min-height:auto;
    }
    button.v-btn{
      display:block;
      width:5rem;
      height:5rem;
      line-height:5rem;
      text-align:center;
      color:var(--color-white);
      background: var(--color-gray4);
    }
  }

  .file-list {
    margin-top: 1rem;
    display:block;
    border:1px solid var(--tbl-header-line);
    border-radius:.5rem;
    .file-chip {
      cursor: pointer;
      user-select: none;
      border-bottom:1px solid var(--tbl-header-line);
      border-radius:0;
      font-size:1.2rem;
      width:100%;
      .v-chip__underlay{
        background:transparent;
      }
      .v-chip__content{
        padding-left:2.5rem;
        background:url("/src/assets/img/ico-filedownload.svg") left center no-repeat;
        background-size:1.6rem auto;
      }
    }
    & .file-item:last-child .file-chip{
      border-bottom:0;
    }
  }
}
</style>
